import Cookies from 'js-cookie';
import { appEnv } from '@/config/env';
import { coordinateTokenRefresh, setOnRemoteRefreshSuccess } from './auth-refresh-coordinator';
import { syncSessionMetadata } from './auth-token-storage';
import store from '@/stores';
import { loggedOut } from '@/stores/authenticated';

export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  headers: Headers;
}

export class ApiError extends Error {
  response?: { status: number; data: unknown };
  constructor(message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestConfig = { headers?: Record<string, string> };

function joinUrl(base: string, path: string): string {
  const trimmedBase = base.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${normalizedPath}`;
}

const REFRESH_HTTP_COOLDOWN_MS = 2500;

/**
 * Vite HMR で api モジュールが再評価されると refreshInFlight が初期化され、
 * 進行中の fetch と新しい performTokenRefresh が並走し unknown + 次リクエスト 401 になり得る。
 * globalThis でタブ内単一フライト、Web Locks + BroadcastChannel でタブ間直列化する。
 */
const REFRESH_STATE_KEY = '__roomiMeetAuthRefreshState' as const;

type AuthRefreshGlobalState = {
  refreshInFlight: Promise<boolean> | null;
  lastSuccessfulHttpRefreshAt: number;
};

setOnRemoteRefreshSuccess(() => {
  authRefreshState().lastSuccessfulHttpRefreshAt = Date.now();
});

function authRefreshState(): AuthRefreshGlobalState {
  const g = globalThis as typeof globalThis & {
    [REFRESH_STATE_KEY]?: AuthRefreshGlobalState;
  };
  if (!g[REFRESH_STATE_KEY]) {
    g[REFRESH_STATE_KEY] = {
      refreshInFlight: null,
      lastSuccessfulHttpRefreshAt: 0,
    };
  }
  return g[REFRESH_STATE_KEY];
}

export type RefreshTokensOnceOptions = {
  /** true のときクールダウンを無視（API 401 後の再発行など） */
  bypassCooldown?: boolean;
};

/** Set-Cookie 反映と後続 fetch の順序を譲るため、成功時のみ 2 ターン待つ */
function delayTwoMicrotasks(): Promise<void> {
  return new Promise<void>((resolve) => {
    queueMicrotask(() => {
      queueMicrotask(() => resolve());
    });
  });
}

/** 廃止した userId Cookie の残骸を削除（移行用） */
export function clearLegacyUserIdCookie(): void {
  Cookies.remove('userId');
}

export async function clearAuthSessionAndRedirect(): Promise<void> {
  try {
    const url = joinUrl(appEnv.apiUrl, '/auth/logout');
    await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
  } catch {
    // オフラインでもクライアント側はクリーンアップする
  }
  clearLegacyUserIdCookie();
  store.dispatch(loggedOut());
  window.location.href = appEnv.dir as string;
}

async function performTokenRefresh(): Promise<boolean> {
  try {
    const url = joinUrl(appEnv.apiUrl, '/auth/refresh');
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      return false;
    }
    const text = await response.text();
    let data: { accessTokenExpiresAt: number };
    try {
      data = JSON.parse(text) as { accessTokenExpiresAt: number };
    } catch {
      return false;
    }
    const now = Date.now();
    let expiresAt = data.accessTokenExpiresAt;
    const serverLooksValid =
      typeof expiresAt === 'number' && Number.isFinite(expiresAt) && expiresAt > now;
    if (!serverLooksValid) {
      expiresAt = now + appEnv.accessTokenClientTtlFallbackMs;
    }
    syncSessionMetadata(expiresAt);
    authRefreshState().lastSuccessfulHttpRefreshAt = Date.now();
    return true;
  } catch {
    return false;
  }
}

/**
 * 多重リフレッシュを防ぎつつ、httpOnly refresh Cookie でアクセストークンを更新する。
 *
 * 成功時: 二重 microtask 待ちのあいだ refreshInFlight を未解決のままにし、並行 401 が同じ Promise を await して
 * リトライだけに回す（Set-Cookie 競合対策）。
 *
 * 失敗時: 解決済み false を共有すると、その数 ms のあいだ後続 401 が再リフレッシュせず即ログアウトするため、
 * 遅延なく finally で null に戻す。
 */
export function refreshTokensOnce(opts?: RefreshTokensOnceOptions): Promise<boolean> {
  const st = authRefreshState();
  if (st.refreshInFlight) {
    return st.refreshInFlight;
  }

  if (!opts?.bypassCooldown) {
    const now = Date.now();
    const exp = store.getState().authenticated.accessTokenExpiresAt;
    if (
      now - st.lastSuccessfulHttpRefreshAt < REFRESH_HTTP_COOLDOWN_MS &&
      typeof exp === 'number' &&
      Number.isFinite(exp) &&
      exp > now
    ) {
      return Promise.resolve(true);
    }
  }

  if (!st.refreshInFlight) {
    const p = coordinateTokenRefresh(performTokenRefresh);
    st.refreshInFlight = p
      .then(async (ok) => {
        if (ok) {
          await delayTwoMicrotasks();
        }
        return ok;
      })
      .finally(() => {
        st.refreshInFlight = null;
      });
  }
  return st.refreshInFlight;
}

function isPublicAuthPath(path: string): boolean {
  const p = path.startsWith('/') ? path : `/${path}`;
  return (
    p === '/auth/refresh' ||
    p === '/auth/signin' ||
    p === '/auth/logout' ||
    // bootstrap 時のセッション有無確認。401 は未ログインであり full redirect の対象外
    p === '/auth/verify' ||
    p.startsWith('/auth/recoverPassword') ||
    p.startsWith('/auth/resetPassword') ||
    /\/room\/\d+\/jitsi-token/.test(p)
  );
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  config?: RequestConfig,
  retriedAfterRefresh = false,
): Promise<ApiResponse<T>> {
  const pNorm = path.startsWith('/') ? path : `/${path}`;
  if (pNorm === '/auth/refresh' && method === 'POST') {
    const ok = await refreshTokensOnce();
    if (!ok) {
      const error = new ApiError('HTTP 401');
      error.response = { status: 401, data: undefined };
      throw error;
    }
    const expiresAt = store.getState().authenticated.accessTokenExpiresAt;
    const accessTokenExpiresAt =
      typeof expiresAt === 'number' && Number.isFinite(expiresAt)
        ? expiresAt
        : Date.now() + appEnv.accessTokenClientTtlFallbackMs;
    return {
      data: { accessTokenExpiresAt } as T,
      status: 200,
      headers: new Headers(),
    };
  }

  const url = joinUrl(appEnv.apiUrl, path);
  const headers = new Headers();
  if (config?.headers) {
    Object.entries(config.headers).forEach(([key, value]) => {
      if (value !== undefined) {
        headers.set(key, value);
      }
    });
  }

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
  };

  if (body !== undefined && method !== 'GET' && method !== 'HEAD') {
    if (body instanceof FormData) {
      init.body = body;
    } else {
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
      init.body = JSON.stringify(body);
    }
  }

  const response = await fetch(url, init);
  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text();

  let data: unknown;
  if (text.length === 0) {
    data = undefined;
  } else if (contentType.includes('application/json')) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  } else {
    data = text;
  }

  if (!response.ok) {
    if (response.status === 401) {
      if (!retriedAfterRefresh && !isPublicAuthPath(path)) {
        const refreshed = await refreshTokensOnce({ bypassCooldown: true });
        if (refreshed) {
          return request<T>(method, path, body, config, true);
        }
      }
      if (!isPublicAuthPath(path)) {
        void clearAuthSessionAndRedirect();
      }
    }
    const error = new ApiError(`HTTP ${response.status}`);
    error.response = { status: response.status, data };
    throw error;
  }

  return {
    data: data as T,
    status: response.status,
    headers: response.headers,
  };
}

export const api = {
  get<T>(path: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('GET', path, undefined, config);
  },
  post<T>(path: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('POST', path, body, config);
  },
  patch<T>(path: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('PATCH', path, body, config);
  },
  delete<T>(path: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('DELETE', path, undefined, config);
  },
};
